<template>
  <div class="py-6 px-4">
    <h2 class="text-xl font-semibold text-gray-800 mb-6">
      {{ dealData?.mode === 'edit' ? 'Modifier le Deal' : 'Nouveau Deal' }}
    </h2>

    <form @submit.prevent="saveDeal" ref="form" class="space-y-6">
      <!-- Sélection du plat -->
      <div class="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
        <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Plat à mettre en promotion
        </h3>

        <!-- Mode création : recherche et sélection -->
        <template v-if="dealData?.mode === 'create'">
          <!-- Recherche -->
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher par nom ou restaurant..."
              class="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-3 text-sm text-gray-800 shadow-theme-xs focus:border-primary-500 focus:outline-hidden focus:ring-3 focus:ring-primary-500/10"
            />
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <!-- Liste des plats filtrés -->
          <div class="max-h-80 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
            <div
              v-for="dish in filteredDishes"
              :key="dish.id"
              @click="selectDish(dish)"
              :class="[
                'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all',
                deal.dishId === dish.id
                  ? 'bg-primary-50 border-primary-300 ring-2 ring-primary-200'
                  : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
              ]"
            >
              <ImgComponent
                :src="dish.principalImageUrl"
                fallback="/images/logo.png"
                class="w-14 h-14 rounded-lg object-cover flex-shrink-0"
              />
              <div class="flex-1 min-w-0">
                <div class="font-semibold text-gray-800 truncate">{{ dish.title }}</div>
                <div class="text-sm text-gray-500 truncate">{{ dish.storeName }}</div>
                <div v-if="dish.category" class="text-xs text-gray-400">{{ dish.category }}</div>
              </div>
              <div class="text-right flex-shrink-0">
                <div class="font-bold text-primary-600">{{ dish.price }} {{ dish.currency }}</div>
                <svg
                  v-if="deal.dishId === dish.id"
                  class="w-5 h-5 text-primary-600 ml-auto mt-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>

            <div v-if="filteredDishes.length === 0" class="text-center py-8 text-gray-500">
              Aucun plat trouvé
            </div>
          </div>
        </template>

        <!-- Aperçu du plat sélectionné -->
        <div v-if="selectedDish" class="bg-gray-50 rounded-xl p-4 flex gap-4">
          <ImgComponent
            :src="selectedDish.principalImageUrl"
            fallback="/images/logo.png"
            class="w-20 h-20 rounded-lg object-cover"
          />
          <div class="flex-1">
            <div class="font-semibold text-gray-800">{{ selectedDish.title }}</div>
            <div class="text-sm text-gray-500">{{ selectedDish.storeName }}</div>
            <div class="text-sm text-gray-600 mt-1">{{ selectedDish.category }}</div>
          </div>
          <div class="text-right">
            <div class="text-lg font-bold text-gray-800">{{ selectedDish.price }} {{ selectedDish.currency }}</div>
          </div>
        </div>
      </div>

      <!-- Réduction -->
      <div class="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
        <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Réduction
        </h3>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700">
              Pourcentage de réduction * (%)
            </label>
            <input
              required
              type="number"
              min="1"
              max="99"
              v-model.number="deal.discount"
              @input="calculateDiscountedPrice"
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs focus:border-primary-500 focus:outline-hidden focus:ring-3 focus:ring-primary-500/10"
              placeholder="Ex: 20"
            />
            <p class="mt-1 text-xs text-gray-500">Entre 1% et 99%</p>
          </div>

          <div v-if="discountedPrice !== null">
            <label class="mb-1.5 block text-sm font-medium text-gray-700">
              Prix réduit
            </label>
            <div class="w-full rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800 font-semibold">
              {{ discountedPrice }} {{ deal.currency }}
            </div>
            <p class="mt-1 text-xs text-green-600">
              Économie de {{ (selectedDish?.price || 0) - discountedPrice }} {{ deal.currency }}
            </p>
          </div>
        </div>
      </div>

      <!-- Durée du deal -->
      <div class="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
        <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Durée de l'offre
        </h3>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700">
              Date de début *
            </label>
            <input
              required
              type="datetime-local"
              v-model="deal.startDate"
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs focus:border-primary-500 focus:outline-hidden focus:ring-3 focus:ring-primary-500/10"
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700">
              Date de fin *
            </label>
            <input
              required
              type="datetime-local"
              v-model="deal.endDate"
              :min="deal.startDate"
              class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs focus:border-primary-500 focus:outline-hidden focus:ring-3 focus:ring-primary-500/10"
            />
            <p class="mt-1 text-xs text-gray-500">
              Durée : {{ dealDuration }}
            </p>
          </div>
        </div>
      </div>

      <!-- Informations supplémentaires -->
      <div class="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
        <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Informations supplémentaires (optionnel)
        </h3>

        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            Description du deal
          </label>
          <textarea
            v-model="deal.description"
            rows="3"
            class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs focus:border-primary-500 focus:outline-hidden focus:ring-3 focus:ring-primary-500/10"
            placeholder="Décrivez les conditions particulières de cette offre..."
          ></textarea>
        </div>
      </div>
    </form>

    <!-- Actions -->
    <div class="flex gap-3 !mt-8 justify-end">
      <button
        type="button"
        @click="$emit('cancel')"
        class="buttonComponent border rounded-xl"
      >
        Annuler
      </button>
      <button
        type="button"
        @click="saveDeal"
        class="buttonComponent border rounded-xl"
      >
        {{ dealData?.mode === 'edit' ? 'Mettre à jour' : 'Créer' }} le Deal
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import { buildStoreDataUrl } from '@/shared/configs/apiConfig.js'

const props = defineProps({
  dealData: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['saved', 'cancel'])

const form = ref(null)
const availableDishes = ref([])
const searchQuery = ref('')
const deal = ref({
  dishId: props.dealData?.dishId || '',
  discount: props.dealData?.discount || 0,
  description: props.dealData?.description || '',
  currency: 'XOF',
  startDate: props.dealData?.startDate || '',
  endDate: props.dealData?.endDate || '',
})

const filteredDishes = computed(() => {
  if (!searchQuery.value) return availableDishes.value
  const query = searchQuery.value.toLowerCase()
  return availableDishes.value.filter(dish =>
    dish.title.toLowerCase().includes(query) ||
    dish.storeName.toLowerCase().includes(query)
  )
})

const selectedDish = computed(() => {
  if (!deal.value.dishId) return null
  return availableDishes.value.find(d => d.id === deal.value.dishId)
})

const discountedPrice = computed(() => {
  if (!selectedDish.value || !deal.value.discount) return null
  const originalPrice = parseInt(selectedDish.value.price) || 0
  const discount = parseInt(deal.value.discount) || 0
  return Math.ceil(originalPrice - (originalPrice * discount) / 100)
})

const dealDuration = computed(() => {
  if (!deal.value.startDate || !deal.value.endDate) return '-'
  const start = new Date(deal.value.startDate)
  const end = new Date(deal.value.endDate)
  const diffMs = end - start
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60))

  if (diffDays > 0) {
    return `${diffDays} jour(s)`
  } else if (diffHours > 0) {
    return `${diffHours} heure(s)`
  }
  return '-'
})

function onDishSelected() {
  // Réinitialiser la description si un nouveau plat est sélectionné
  if (!props.dealData?.dishId) {
    deal.value.description = ''
  }
}

function selectDish(dish) {
  deal.value.dishId = dish.id
  // Réinitialiser la description si un nouveau plat est sélectionné
  if (!props.dealData?.dishId) {
    deal.value.description = ''
  }
}

function calculateDiscountedPrice() {
  // Le computed se met à jour automatiquement
}

function saveDeal() {
  if (!form.value.checkValidity()) {
    form.value.reportValidity()
    return
  }

  if (!selectedDish.value) {
    alert('Veuillez sélectionner un plat')
    return
  }

  // Construire l'objet deal à partir du plat sélectionné
  const dealPayload = {
    action: props.dealData?.mode === 'edit' ? 'update' : 'create',
    deal: {
      id: props.dealData?.id || `deal_${Date.now()}`,
      // Copier toutes les propriétés du plat
      ...selectedDish.value,
      // Ajouter/écraser avec les propriétés du deal
      dishId: selectedDish.value.id,
      discount: deal.value.discount,
      discountedPrice: discountedPrice.value,
      description: deal.value.description || selectedDish.value.description,
      brand: selectedDish.value.id, // ID du plat original
      // Durée du deal
      startDate: new Date(deal.value.startDate).toISOString(),
      endDate: new Date(deal.value.endDate).toISOString(),
    },
  }

  emit('saved', dealPayload)
}

// Charger les plats disponibles
onMounted(async () => {
  try {
    const response = await fetch(
      buildStoreDataUrl({ userId: 'rapidoo-data-mock' })
    )
    const data = await response.json()

    if (data?.data?.appInfo?.dishes?.data) {
      const dishesData = Array.isArray(data.data.appInfo.dishes.data)
        ? data.data.appInfo.dishes.data
        : [data.data.appInfo.dishes.data]

      availableDishes.value = dishesData.map(dish => ({
        id: dish.id,
        title: dish.title,
        price: dish.price,
        currency: dish.currency || 'XOF',
        storeName: dish.storeName,
        storeId: dish.storeId,
        category: dish.category,
        categoryId: dish.categoryId,
        subcategory: dish.subcategory,
        subcategoryId: dish.subcategoryId,
        principalImageUrl: dish.principalImageUrl,
        additionalImageUrl: dish.additionalImageUrl || [],
        description: dish.description,
        options: dish.options || [],
        accompaniements: (dish.metadata?.accompaniements || []).map(acc => ({
          image: acc.image,
          amount: acc.amount,
          name: acc.name,
        })),
        cookDurationInMin: dish.metadata?.cookDurationInMin || 0,
      }))
    }
  } catch (error) {
    console.error('Erreur lors du chargement des plats:', error)
  }
})
</script>

<style scoped>
.bg-primary {
  background-color: #4f46e5;
}
.text-primary {
  color: #4f46e5;
}
.border-primary {
  border-color: #4f46e5;
}
.focus\:ring-primary\/10:focus {
  --tw-ring-color: rgb(79 70 229 / 0.1);
}

.bg-primary-50 {
  background-color: rgb(239 246 255);
}
.border-primary-300 {
  border-color: rgb(196 181 253);
}
.ring-primary-200 {
  --tw-ring-color: rgb(221 214 254);
}
.text-primary-600 {
  color: rgb(79 70 229);
}

/* Scrollbar personnalisée */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
